import { HttpResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { AppSettingsService } from './app-settings.service';

/**
 * Type of support channel for chat support.
 * @readonly
 * @enum {number}
 */
export enum ChatSupportChannel {
  /** Text only. */
  Text  = 1,

  /** Text and voice only. */
  Voice = 2,

  /** Text, voice and video. */
  Video = 3,
}

/**
 * Configuration for chat support.
 */
export interface IChatConfiguration {
  /** Indicates whether chat support is enabled. */
  isEnabled: boolean;

  /**
   * Indicates the current chat support channel.
   * @see ChatSupportChannel
   */
  supportChannel: ChatSupportChannel;

  /** Indicates whether admin has disabled chat. */
  isAdminDisabled?: boolean;
}

/** Chat settings service to manage global chat configuration. */
@Injectable({
  providedIn: 'root'
})
export class ChatSettingsService {

  private _lock = new EventEmitter();
  private _isLoading: boolean = false;

  private _config: IChatConfiguration;

  /**
   * Gets a value indicating whether global chat config is loaded.
   * @return Value indicating whether global chat config is loaded.
   */
  private get _isConfigLoaded(): boolean {
    return this._config !== undefined;
  }

  /**
   * Gets a value indicating whether Sabha is installed.
   * @return Value indicating whether Sabha is installed.
   */
  get isEnabled(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.getConfig().then(
        (chatConfig: IChatConfiguration) => {
          resolve(chatConfig.isEnabled);
        },
        error => {
          reject(error);
        }
      );
    });
  }

  /**
   * Gets a value indicating selected chat support channel.
   * @return Value indicating selected chat support channel.
   */
  get supportChannel(): Promise<ChatSupportChannel> {
    return new Promise<ChatSupportChannel>((resolve, reject) => {
      this.getConfig().then(
        (chatConfig: IChatConfiguration) => {
          resolve(chatConfig.supportChannel);
        },
        error => {
          reject(error);
        }
      );
    });
  }

  /**
   * @param _appSettings App settings service
   */
  constructor(private _appSettings: AppSettingsService) { }

  /**
   * Gets global chat configuration.
   */
  public getConfig = (resetCache : boolean = false): Promise<IChatConfiguration> => {
    return new Promise<IChatConfiguration>(async (resolve, reject) => {
      if (this._isLoading) {
        await new Promise(resolve => this._lock.subscribe(resolve))
      }
      if (this._isConfigLoaded && !resetCache) {
        resolve(this._config);
      } else {
        this._isLoading = true;

        this._appSettings.getChatConfiguration$().subscribe(
          (chatConfig: IChatConfiguration) => {
            this._config = chatConfig;

            this._isLoading = false;
            this._lock.emit();
            resolve(chatConfig);
          },
          error => {
            this._isLoading = false;
            this._lock.emit();
            reject(error);
          }
        );
      }
    });
  }

  /**
   * Updates global chat configuration.
   */
  public updateConfig = (chatConfig: IChatConfiguration): Promise<HttpResponse<IChatConfiguration>> => {
    return new Promise<HttpResponse<IChatConfiguration>>((resolve, reject) => {
      this._appSettings.updateChatConfiguration$(chatConfig).subscribe(
        (response: HttpResponse<IChatConfiguration>) => {
          this._config = response.body;
          resolve(response);
        },
        error => {
          reject(error);
        }
      );
    });
  }
}
